import { Link } from "wouter";
import { ADDRESSES, addressUrl, short } from "../lib/deployment.js";

/**
 * The public landing page. PRD §22.1.
 *
 * It leads with the gap rather than with the product, because the gap is the part most people have
 * not thought about: every stablecoin rail protects your principal and none of them deliver the
 * money you owed. Getting a refund and paying your staff are different outcomes, and the whole
 * protocol exists in the space between them.
 *
 * Every figure below comes from a run on Arc, including the one where SYRTY failed to deliver. That
 * case is on the page on purpose. A settlement guarantee that only shows its wins is a marketing
 * claim; showing the shortfall is what makes the successful cures mean anything.
 */
export function Landing() {
  return (
    <>
      <section className="surface-canvas">
        <div className="page section-tall grid" style={{ gridTemplateColumns: "1.1fr 0.9fr", alignItems: "center" }}>
          <div className="stack" style={{ gap: "var(--spacing-36)" }}>
            <h1>
              A refund is not
              <br />a payment.
            </h1>
            <p style={{ fontSize: 18, lineHeight: 1.25, maxWidth: "46ch" }}>
              When a stablecoin settlement fails, every rail available today returns your money.
              None of them deliver the euros you owed. SYRTY is the layer that does: bonded standby
              providers, selected so they cannot all fail together, who complete the payment when the
              primary does not.
            </p>
            <div className="row">
              <Link href="/mandates/new" className="button-primary">
                Create a mandate
              </Link>
              <Link href="/evidence" className="button-ghost">
                See the proof
              </Link>
            </div>
            <p className="caption">
              Live on Arc Public Testnet. Every number on this page comes from a transaction you can
              open in a block explorer.
            </p>
          </div>

          <div className="stack">
            <div className="card-data stack" style={{ gap: "var(--spacing-12)" }}>
              <span className="label">Scenario C, on Arc</span>
              <span className="polysans" style={{ fontSize: 40, lineHeight: 1.2, letterSpacing: "-0.8px" }}>
                0.18 EURC
              </span>
              {/*
                Shown as a token amount rather than as the raw 180,000, which is what the contracts
                store and what an earlier version of this page displayed. Six decimals means 180,000
                base units is eighteen hundredths of a euro, and a hero figure a reader has to
                convert in their head is a hero figure that gets read as a hundred and eighty
                thousand. The base units are kept beside it because that is what the receipt says.
              */}
              <p style={{ fontSize: 14 }}>
                the recipient did not receive after every standby tier failed —{" "}
                <span className="mono">180,000</span> base units. The protocol reported{" "}
                <span className="tag tag-accent">SHORTFALL</span> rather than a success.
              </p>
              <hr className="divider" />
              <span className="label">Recovery outstanding</span>
              <span className="polysans figure" style={{ fontSize: 32, letterSpacing: "-0.64px" }}>
                0
              </span>
              <p style={{ fontSize: 14 }}>
                Both bonds fully covered their claims. Financial reconciliation was complete and the
                payroll was still short. These are two different numbers and SYRTY never merges them.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="surface-ash">
        <div className="page section-tall stack" style={{ gap: "var(--spacing-40)" }}>
          <h2>Three things sit between a failed payment and a paid recipient</h2>
          <div className="grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
            <Layer
              name="StableFX"
              role="Protects the exchange"
              detail="Holds risk buffers from both parties. If one funds and the other does not, the funding party gets its money and the counterparty's buffer back. Principal protected. Nothing delivered."
            />
            <Layer
              name="Settlement Advance"
              role="Funds the maker"
              detail="A maker-facing credit product. It changes who is out of pocket while a trade settles. It does not make a failed trade complete."
            />
            <Layer
              name="SYRTY"
              role="Completes the obligation"
              detail="A different provider delivers the target currency to the same recipient before the same deadline, backed by a bond that is slashed when they do not."
              accent
            />
          </div>
        </div>
      </section>

      <section className="surface-canvas">
        <div className="page section-tall stack" style={{ gap: "var(--spacing-40)" }}>
          <h2>How the guarantee actually holds</h2>
          <div className="grid" style={{ gridTemplateColumns: "repeat(2, 1fr)", gap: "var(--spacing-40)" }}>
            <Step
              index="01"
              title="Capacity is bought before it is needed"
              body="Providers commit delivery inventory and a performance bond to a specific mandate through a sealed-bid auction. Committed inventory is locked in a vault and cannot be spent on anything else. A promise nobody has reserved capital against is not capacity."
            />
            <Step
              index="02"
              title="The portfolio is checked for correlation, not just price"
              body="A cheaper portfolio whose primary and standby share a custodian is refused on chain, with the two offending providers named. The buyer sees the rejected portfolio next to the selected one and why the protocol would not accept it."
            />
            <Step
              index="03"
              title="Anyone can activate the cure"
              body="Breach eligibility is derived from contract state and elapsed time, never from a webhook or an operator's word. Any wallet with gas can call activateCure and be paid a bounty for it. No single operator's uptime decides whether a guarantee completes."
            />
            <Step
              index="04"
              title="Failure is reported as failure"
              body="When every tier fails, the receipt says SHORTFALL, reports zero delivered, and does not claim the SLA was met. Recovery from provider bonds is a separate figure that never implies the recipient was paid."
            />
          </div>
        </div>
      </section>

      <section className="surface-ivory">
        <div className="page section-tall stack" style={{ gap: "var(--spacing-20)" }}>
          <h3>Built on Arc, in the currency it settles in</h3>
          <p style={{ maxWidth: "70ch", fontSize: 18, lineHeight: 1.25 }}>
            Gas on Arc is native USDC, so a provider's bond, the buyer's source funds and the cost of
            activating a cure are all denominated in the same asset. Sub-second blocks with
            deterministic finality are what make a standby response window measured in blocks a
            deadline rather than an estimate.
          </p>
          <p className="caption">
            Arc&apos;s USDC is precompile-backed, which means no external EVM can execute it. Every
            live proof in this project runs against the node itself rather than a local fork.
          </p>
        </div>
      </section>

      <section className="surface-canvas">
        <div className="page section-tall stack" style={{ gap: "var(--spacing-20)" }}>
          <h3>Deployed contracts</h3>
          <p className="caption">
            Canonical deployment on Arc Public Testnet, chain 5042002. Every address is verifiable.
          </p>
          <div className="grid" style={{ gridTemplateColumns: "repeat(2, 1fr)", gap: "var(--spacing-8)" }}>
            {["MandateBook", "SettlementEscrow", "CureCoordinator", "SelectionVerifier", "ReceiptBook", "ProviderRiskGraph"].map(
              (name) => (
                <a
                  key={name}
                  className="row"
                  href={addressUrl(ADDRESSES[name] ?? "")}
                  target="_blank"
                  rel="noreferrer"
                  style={{ justifyContent: "space-between", textDecoration: "none", padding: "8px 0" }}
                >
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{name}</span>
                  <span className="mono">{short(ADDRESSES[name] ?? "")}</span>
                </a>
              ),
            )}
          </div>
          <p className="caption">
            The StableFX adapter is deployed-ready and deliberately disabled. Without issued TEST
            credentials it reports{" "}
            <span className="tag tag-brass">ADAPTER_COMPLETE_ACCESS_GATED</span> rather than
            pretending to integrate. See{" "}
            <Link href="/evidence" className="link-accent">
              the evidence page
            </Link>
            .
          </p>
        </div>
      </section>
    </>
  );
}

function Layer({
  name,
  role,
  detail,
  accent = false,
}: {
  name: string;
  role: string;
  detail: string;
  accent?: boolean;
}) {
  return (
    <div className="card-asymmetric stack" style={{ background: accent ? "var(--color-canvas-white)" : undefined }}>
      <span className={accent ? "tag tag-accent" : "tag"}>{role}</span>
      <h4>{name}</h4>
      <p style={{ fontSize: 14, lineHeight: 1.5 }}>{detail}</p>
    </div>
  );
}

function Step({ index, title, body }: { index: string; title: string; body: string }) {
  return (
    <div className="stack" style={{ gap: "var(--spacing-12)" }}>
      <span className="label" style={{ color: "var(--color-brass)" }}>
        {index}
      </span>
      <h4>{title}</h4>
      <p style={{ fontSize: 15, lineHeight: 1.5 }}>{body}</p>
    </div>
  );
}
